import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  applySanctionRatings,
  closeSanctionUnrated,
  confirmSanctionFacts,
  decideSanction,
  getSanction,
  previewSanctionRatings,
  returnSanctionResults,
  revokeSanction,
  type TournamentSanction,
} from "../api";
import {
  CONFIRMABLE,
  DENSE_CONTROL,
  DENSE_ICON,
  REVOCABLE,
  STATUS_LABEL,
  STATUS_TONE,
  errorMessage,
  formatDateTime,
  formatDay,
  hasStarted,
  isRevisionConflict,
} from "../sanctionDisplay";
import { RatingsPreviewTable } from "./RatingsPreviewTable";
import {
  PanelSection,
  SanctionCheckList,
  SanctionFactList,
  SanctionSteps,
  changedFactLabels,
} from "./SanctionFacts";
import { SanctionNoteDialog } from "./SanctionNoteDialog";

export const SANCTIONS_QUERY_KEY = [
  "platform",
  "tournament-sanctions",
] as const;
const MAX_CONDITIONS = 10;
const MAX_CONDITION = 300;

type NoteAction = "reject" | "request_changes" | "return" | "close" | "revoke";
type Action =
  | { kind: "approve"; conditions: string[] }
  | { kind: NoteAction; note: string }
  | { kind: "confirm_facts" }
  | { kind: "apply"; idempotencyKey: string };

const NOTE_DIALOG: Record<
  NoteAction,
  {
    title: string;
    description: string;
    label: string;
    confirm: string;
    destructive?: boolean;
  }
> = {
  reject: {
    title: "Reject sanction request",
    description:
      "The club sees this reason. They can send a new request before the tournament starts.",
    label: "Reason for the club",
    confirm: "Reject",
    destructive: true,
  },
  request_changes: {
    title: "Request changes",
    description:
      "The club sees this note and can resubmit the same request before the tournament starts.",
    label: "What the club should change",
    confirm: "Request changes",
  },
  return: {
    title: "Return results for correction",
    description:
      "The results go back to the club. They correct, re-certify and submit again.",
    label: "What to fix",
    confirm: "Return results",
  },
  close: {
    title: "Close without ratings",
    description:
      "The tournament stays an official BW event, but its results never reach BW ratings. The club sees this note.",
    label: "Why no ratings apply",
    confirm: "Close without ratings",
    destructive: true,
  },
  revoke: {
    title: "Revoke sanction",
    description:
      "The tournament stops being an official BW event and its results won't count. The club sees this reason.",
    label: "Reason shown to the club",
    confirm: "Revoke sanction",
    destructive: true,
  },
};

const SUCCESS: Record<Action["kind"], string> = {
  approve: "Sanctioned. The club was notified.",
  reject: "Request rejected. The club was notified.",
  request_changes: "Changes requested. The club was notified.",
  return: "Results returned to the club.",
  close: "Closed without ratings.",
  revoke: "Sanction revoked. The club was notified.",
  confirm_facts: "Current facts confirmed.",
  apply: "Ratings applied.",
};

function run(
  sanction: TournamentSanction,
  action: Action,
): Promise<TournamentSanction> {
  const id = sanction.sanction_id;
  const expected_revision = sanction.revision;
  switch (action.kind) {
    case "approve":
      return decideSanction(id, {
        decision: "approve",
        expected_revision,
        conditions: action.conditions,
      });
    case "reject":
    case "request_changes":
      return decideSanction(id, {
        decision: action.kind,
        expected_revision,
        conditions: [],
        note: action.note,
      });
    case "return":
      return returnSanctionResults(id, {
        expected_revision,
        note: action.note,
      });
    case "close":
      return closeSanctionUnrated(id, { expected_revision, note: action.note });
    case "revoke":
      return revokeSanction(id, { expected_revision, reason: action.note });
    case "confirm_facts":
      return confirmSanctionFacts(id, { expected_revision });
    case "apply":
      return applySanctionRatings(id, {
        expected_revision,
        idempotency_key: action.idempotencyKey,
      });
  }
}

function ConditionsEditor({
  conditions,
  onChange,
  disabled,
}: {
  conditions: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const text = draft.trim();
    if (!text || conditions.length >= MAX_CONDITIONS) return;
    onChange([...conditions, text]);
    setDraft("");
  };
  return (
    <Box>
      <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
        {conditions.map((condition, index) => (
          <Box
            component="li"
            key={`${index}-${condition}`}
            sx={{ fontSize: 13, py: 0.25 }}
          >
            <Stack direction="row" sx={{ alignItems: "flex-start" }}>
              <Typography component="span" sx={{ fontSize: 13, flex: 1 }}>
                {condition}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove condition ${index + 1}`}
                disabled={disabled}
                onClick={() =>
                  onChange(conditions.filter((_, i) => i !== index))
                }
                sx={{ p: 0.25, ...DENSE_ICON }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Box>
      {conditions.length < MAX_CONDITIONS ? (
        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Add a condition, e.g. Results within 72 hours"
            value={draft}
            disabled={disabled}
            onChange={(event) =>
              setDraft(event.target.value.slice(0, MAX_CONDITION))
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
            slotProps={{ htmlInput: { "aria-label": "New condition" } }}
          />
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={add}
            disabled={disabled || !draft.trim()}
          >
            Add
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}

export function SanctionDetailPanel({
  sanctionId,
  onClose,
}: {
  sanctionId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const detailKey = [...SANCTIONS_QUERY_KEY, "detail", sanctionId] as const;
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => getSanction(sanctionId),
  });
  const sanction = detail.data;
  const previewEnabled =
    sanction?.status === "results_submitted" && sanction.results_stale !== true;
  const preview = useQuery({
    queryKey: [
      ...SANCTIONS_QUERY_KEY,
      "preview",
      sanctionId,
      sanction?.revision,
    ],
    queryFn: () => previewSanctionRatings(sanctionId),
    enabled: previewEnabled,
    retry: false,
  });

  const [conditions, setConditions] = useState<string[]>([]);
  const [noteAction, setNoteAction] = useState<NoteAction | null>(null);
  const [applyKey, setApplyKey] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [notice, setNotice] = useState<{
    severity: "success" | "info" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    setConditions([]);
    setNotice(null);
  }, [sanctionId]);

  const mutation = useMutation({
    mutationFn: (action: Action) => run(sanction!, action),
    retry: false,
    onSuccess: async (updated, action) => {
      queryClient.setQueryData(detailKey, updated);
      setNoteAction(null);
      setApplyKey(null);
      setConditions([]);
      setNotice({ severity: "success", text: SUCCESS[action.kind] });
      await queryClient.invalidateQueries({ queryKey: SANCTIONS_QUERY_KEY });
    },
    onError: async (error) => {
      setNoteAction(null);
      setApplyKey(null);
      if (isRevisionConflict(error)) {
        // The sanction changed underneath: never let one more click approve
        // a stale draft against the refreshed request.
        setConditions([]);
        setNotice({
          severity: "info",
          text: "Someone else changed this sanction. The latest version is shown; review it and try again.",
        });
        await queryClient.invalidateQueries({ queryKey: SANCTIONS_QUERY_KEY });
        return;
      }
      setNotice({ severity: "error", text: errorMessage(error) });
    },
  });
  const pending = mutation.isPending;

  let body: ReactNode;
  if (detail.isLoading) {
    body = (
      <Box
        role="status"
        aria-label="Loading sanction"
        sx={{ display: "flex", justifyContent: "center", py: 6 }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  } else if (detail.isError || !sanction) {
    body = (
      <Alert severity="error" sx={{ m: 2 }}>
        This sanction couldn&apos;t be loaded.
      </Alert>
    );
  } else {
    const status = sanction.status;
    const changed = changedFactLabels(sanction.facts_changed);
    const checks = sanction.checks;
    const failingChecks = checks
      ? Object.values(checks).filter((ok) => !ok).length
      : 0;
    const started = hasStarted(sanction);
    const clubNote =
      status === "revoked" || status === "completed_unrated"
        ? sanction.close_note
        : status === "rejected" || status === "changes_requested"
          ? sanction.decision_note
          : status === "sanctioned"
            ? sanction.returned_note
            : null;
    body = (
      <>
        {/* Notices sit above the scroll region so an action's outcome is always in view. */}
        <Box
          role="region"
          aria-label="Sanction notices"
          aria-live="polite"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            px: 2,
            "&:not(:empty)": {
              py: 1.25,
              borderBottom: 1,
              borderColor: "divider",
            },
          }}
        >
          {notice ? (
            <Alert
              severity={notice.severity}
              onClose={() => setNotice(null)}
              sx={{ fontSize: 13 }}
            >
              {notice.text}
            </Alert>
          ) : null}
          {changed.length > 0 && CONFIRMABLE.includes(status) ? (
            <Alert
              severity="warning"
              sx={{ fontSize: 13 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={pending}
                  onClick={() => mutation.mutate({ kind: "confirm_facts" })}
                >
                  Confirm facts
                </Button>
              }
            >
              Changed since{" "}
              {status === "requested" ? "the request" : "approval"}:{" "}
              {changed.join(", ")}.{" "}
              {status === "requested"
                ? "Review the tournament and confirm its current facts."
                : "Confirm the current facts before applying ratings."}
            </Alert>
          ) : null}
          {sanction.results_stale ? (
            <Alert severity="warning" sx={{ fontSize: 13 }}>
              The club corrected results after submitting them. Ratings
              can&apos;t be applied until they resubmit, or return the results
              to them.
            </Alert>
          ) : null}
          {clubNote ? (
            <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
              <b>Note to the club:</b> {clubNote}
            </Alert>
          ) : null}
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            "& > section:first-of-type": { borderTop: 0 },
          }}
        >
          {sanction.request_note ? (
            <PanelSection title="Club note">
              <Typography sx={{ fontSize: 13 }}>
                {sanction.request_note}
              </Typography>
            </PanelSection>
          ) : null}

          {status !== "requested" &&
          status !== "changes_requested" &&
          status !== "rejected" &&
          status !== "withdrawn" ? (
            <PanelSection title="Status">
              <SanctionSteps sanction={sanction} />
            </PanelSection>
          ) : null}

          <PanelSection title="Facts">
            <SanctionFactList sanction={sanction} />
          </PanelSection>

          {checks ? (
            <PanelSection
              title="Checks"
              aside={
                failingChecks ? (
                  <Typography color="error.main" sx={{ fontSize: 12 }}>
                    {failingChecks} failing
                  </Typography>
                ) : null
              }
            >
              <SanctionCheckList checks={checks} />
            </PanelSection>
          ) : null}

          {status === "requested" ? (
            <PanelSection
              title="Conditions"
              aside={
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  Optional
                </Typography>
              }
            >
              <ConditionsEditor
                key={sanction.revision}
                conditions={conditions}
                onChange={setConditions}
                disabled={pending}
              />
            </PanelSection>
          ) : (sanction.conditions ?? []).length ? (
            <PanelSection title="Conditions">
              <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                {(sanction.conditions ?? []).map((condition, index) => (
                  <Box
                    component="li"
                    key={`${index}-${condition}`}
                    sx={{ fontSize: 13, py: 0.25 }}
                  >
                    {condition}
                  </Box>
                ))}
              </Box>
            </PanelSection>
          ) : null}

          {status === "results_submitted" ? (
            <PanelSection title="Rating preview">
              {sanction.results_stale ? (
                <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                  No preview while the results are out of date.
                </Typography>
              ) : (
                <RatingsPreviewTable
                  preview={preview.data}
                  isLoading={preview.isLoading}
                  error={preview.error}
                />
              )}
            </PanelSection>
          ) : null}
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            px: 2,
            py: 1.25,
            borderTop: 1,
            borderColor: "divider",
            alignItems: "center",
          }}
        >
          {status === "requested" ? (
            <>
              <Button
                color="error"
                variant="outlined"
                size="small"
                disabled={pending}
                onClick={() => setNoteAction("reject")}
                sx={DENSE_CONTROL}
              >
                Reject
              </Button>
              <Tooltip
                title={
                  started
                    ? "The tournament has started, so the club can't resubmit."
                    : ""
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    disabled={pending || started}
                    onClick={() => setNoteAction("request_changes")}
                    sx={{ whiteSpace: "nowrap", ...DENSE_CONTROL }}
                  >
                    Request changes
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : null}
          {status === "results_submitted" ? (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={pending}
              onClick={() => setNoteAction("return")}
              sx={{ whiteSpace: "nowrap", ...DENSE_CONTROL }}
            >
              Return for correction
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {status === "requested" ? (
            <Tooltip
              title={
                failingChecks ? "Every check must pass before sanctioning." : ""
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<VerifiedOutlinedIcon />}
                  disabled={pending || failingChecks > 0}
                  onClick={() =>
                    mutation.mutate({ kind: "approve", conditions })
                  }
                  sx={{ whiteSpace: "nowrap", ...DENSE_CONTROL }}
                >
                  {conditions.length
                    ? `Sanction with ${conditions.length} ${conditions.length === 1 ? "condition" : "conditions"}`
                    : "Sanction"}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {status === "results_submitted" ? (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<DoneAllIcon />}
              disabled={
                pending ||
                Boolean(sanction.results_stale) ||
                changed.length > 0 ||
                !preview.data
              }
              onClick={() => setApplyKey(crypto.randomUUID())}
              sx={{ whiteSpace: "nowrap", ...DENSE_CONTROL }}
            >
              Apply ratings
            </Button>
          ) : null}
        </Stack>

        {noteAction ? (
          <SanctionNoteDialog
            open
            title={NOTE_DIALOG[noteAction].title}
            description={NOTE_DIALOG[noteAction].description}
            label={NOTE_DIALOG[noteAction].label}
            confirmLabel={NOTE_DIALOG[noteAction].confirm}
            destructive={NOTE_DIALOG[noteAction].destructive}
            pending={pending}
            onCancel={() => setNoteAction(null)}
            onConfirm={(note) => mutation.mutate({ kind: noteAction, note })}
          />
        ) : null}
        <Dialog
          open={applyKey !== null}
          onClose={pending ? undefined : () => setApplyKey(null)}
          aria-labelledby="apply-ratings-title"
        >
          <DialogTitle
            id="apply-ratings-title"
            sx={{ fontSize: 16, fontWeight: 700 }}
          >
            Apply ratings
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Write these results to BW ratings for{" "}
              {preview.data?.rows.length ?? 0} players? This can&apos;t be
              undone, and the sanction can no longer be revoked afterwards.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Results submitted {formatDateTime(sanction.results_submitted_at)}.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              color="inherit"
              disabled={pending}
              onClick={() => setApplyKey(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={pending || applyKey === null}
              onClick={() =>
                applyKey &&
                mutation.mutate({ kind: "apply", idempotencyKey: applyKey })
              }
            >
              Apply ratings
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Box
      component="aside"
      aria-label="Sanction details"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 1.25,
          pb: 1.25,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <EmojiEventsOutlinedIcon
            sx={{ fontSize: 16, color: "text.secondary" }}
          />
          <Typography color="text.secondary" sx={{ fontSize: 12.5, flex: 1 }}>
            {sanction &&
            [
              "requested",
              "changes_requested",
              "rejected",
              "withdrawn",
            ].includes(sanction.status)
              ? "Sanction request"
              : "Sanctioned event"}
          </Typography>
          {sanction &&
          (REVOCABLE.includes(sanction.status) ||
            sanction.status === "sanctioned" ||
            sanction.status === "results_submitted") ? (
            <>
              <IconButton
                size="small"
                aria-label="More actions"
                aria-haspopup="menu"
                disabled={mutation.isPending}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={DENSE_ICON}
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                {sanction.status === "sanctioned" ||
                sanction.status === "results_submitted" ? (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      setNoteAction("close");
                    }}
                  >
                    Close without ratings
                  </MenuItem>
                ) : null}
                {REVOCABLE.includes(sanction.status) ? (
                  <MenuItem
                    sx={{ color: "error.main" }}
                    onClick={() => {
                      setMenuAnchor(null);
                      setNoteAction("revoke");
                    }}
                  >
                    Revoke sanction
                  </MenuItem>
                ) : null}
              </Menu>
            </>
          ) : null}
          <IconButton
            size="small"
            aria-label="Close details"
            onClick={onClose}
            sx={{ mr: -0.75, ...DENSE_ICON }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {sanction ? (
          <>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
            >
              <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700 }}>
                {sanction.tournament_name}
              </Typography>
              <Chip
                size="small"
                color={STATUS_TONE[sanction.status]}
                variant="outlined"
                label={STATUS_LABEL[sanction.status]}
                sx={{ height: 22 }}
              />
              {sanction.results_overdue ? (
                <Chip
                  size="small"
                  color="error"
                  label="Results overdue"
                  sx={{ height: 22 }}
                />
              ) : null}
              {sanction.results_late && sanction.status !== "sanctioned" ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="Submitted late"
                  sx={{ height: 22 }}
                />
              ) : null}
            </Stack>
            <Typography
              color="text.secondary"
              sx={{ fontSize: 12.5, mt: 0.25 }}
            >
              <Link
                component={RouterLink}
                to={`/organizations/${sanction.org_id}`}
                underline="hover"
              >
                {sanction.org_name}
              </Link>
              {" · "}Submitted {formatDay(sanction.submitted_at)}
              {sanction.submitted_by_display_name
                ? ` by ${sanction.submitted_by_display_name}`
                : ""}
            </Typography>
          </>
        ) : null}
      </Box>
      {body}
    </Box>
  );
}
